import { useCallback, useMemo, useRef, useState } from 'react';

import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  ListSubheader,
  MenuItem,
  Select,
  type SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { ChevronDown, X } from 'lucide-react';
import {
  type Control,
  Controller,
  type ControllerRenderProps,
} from 'react-hook-form';

import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

export type RaSelectOption = {
  value: string | number | null;
  label: string;
  disabled?: boolean;
  isHeader?: boolean;
};

type RaSelectProps = {
  clearable?: boolean;
  name: string;
  label?: string;
  control: Control<any>;
  options: RaSelectOption[];
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  loading?: boolean;
  helpText?: string;
} & (
  | {
      toggleButtons?: false;
      multiple?: boolean;
      renderValue?: (value: any) => React.ReactNode;
      renderOption?: (option: RaSelectOption) => React.ReactNode;
      emptyValueAsNull?: boolean;
      matchMenuWidth?: boolean;
    }
  | {
      toggleButtons: true;
    }
);

export const RaSelectChevronIcon = ({
  className,
  ...rest
}: {
  className?: string;
}) => (
  <Box
    component="span"
    className={className}
    {...rest}
    sx={{ display: 'flex', alignItems: 'center', color: customPalette.gray600 }}
  >
    <ChevronDown size={16} />
  </Box>
);

export const SelectField: React.FC<
  Omit<
    RaSelectProps & { toggleButtons: false },
    'name' | 'label' | 'control' | 'toggleButtons'
  > & { field: ControllerRenderProps }
> = ({
  field,
  options,
  disabled = false,
  multiple = false,
  renderValue,
  renderOption,
  placeholder,
  loading = false,
  clearable = false,
  emptyValueAsNull = false,
  matchMenuWidth = false,
}) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [menuWidth, setMenuWidth] = useState<number>();

  const computeValue = useCallback(
    (value: any) => {
      if (multiple) {
        if (value == null) {
          return [];
        }
        if (!Array.isArray(value)) {
          return [value];
        }
      }
      return value;
    },
    [multiple],
  );

  const handleChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      if (emptyValueAsNull && event.target.value === '') {
        field.onChange(null);
        return;
      }
      field.onChange(event);
    },
    [field, emptyValueAsNull],
  );

  const isEmpty = useCallback(
    (value: any) => {
      const computedVal = computeValue(value);

      return (
        computedVal == null ||
        (multiple ? computedVal.length === 0 : computedVal === '')
      );
    },
    [multiple, computeValue],
  );

  // Custom renderValue function with ellipsis
  const defaultRenderValue = useCallback(
    (value: any) => {
      if (placeholder && (value === '' || value == null)) {
        return (
          <Typography sx={{ color: 'text.disabled', fontSize: 'inherit' }}>
            {placeholder}
          </Typography>
        );
      }

      if (!value && value !== 0) {
        return '';
      }

      // Get labels for selected values (handle both single and multiple)
      const labels = Array.isArray(value)
        ? value
            .map(v => options.find(o => o.value === v)?.label || v)
            .join(', ')
        : options.find(o => o.value === value)?.label || value;

      return (
        <Typography
          noWrap
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: 'inherit',
          }}
        >
          {labels}
        </Typography>
      );
    },
    [options, placeholder],
  );

  const IconComponent = useMemo(() => {
    if (loading) {
      return () => (
        <Box
          sx={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            opacity: 0.5,
          }}
        >
          <CircularProgress
            size={16}
            sx={{ color: 'action.active' }}
            disableShrink
          />
        </Box>
      );
    }
    return RaSelectChevronIcon;
  }, [loading]);

  return (
    <Select
      {...field}
      ref={(node: any) => {
        anchorRef.current = node;
        if (typeof field.ref === 'function') {
          field.ref(node);
        }
      }}
      onChange={handleChange}
      onOpen={
        matchMenuWidth
          ? () => setMenuWidth(anchorRef.current?.offsetWidth)
          : undefined
      }
      value={computeValue(field.value) ?? ''}
      fullWidth
      variant="outlined"
      size="small"
      sx={{
        background: 'var(--mui-palette-background-paper)',
        '& .MuiSelect-select > *': {
          minWidth: 0,
        },
      }}
      disabled={disabled || loading}
      multiple={multiple}
      displayEmpty
      renderValue={renderValue || defaultRenderValue}
      IconComponent={IconComponent}
      MenuProps={{
        slotProps: {
          paper: {
            sx: {
              ...(matchMenuWidth
                ? {
                    width: menuWidth,
                    '& .MuiMenuItem-root': {
                      minWidth: 0,
                    },
                  }
                : {}),
              '& .MuiListSubheader-root': {
                fontSize: '0.75rem',
              },
            },
          },
        },
      }}
      endAdornment={
        clearable &&
        !isEmpty(field.value) && (
          <IconButton
            size="small"
            sx={{ mr: 1.5 }}
            onClick={e => {
              e.stopPropagation();
              field.onChange(null);
            }}
          >
            <X size={16} />
          </IconButton>
        )
      }
    >
      {options.map(option => {
        if (renderOption != null) {
          return renderOption(option);
        }

        return option.isHeader ? (
          <ListSubheader key={option.value}>{option.label}</ListSubheader>
        ) : (
          <MenuItem
            key={option.value}
            value={option.value ?? ''}
            disabled={option.disabled ?? false}
          >
            {option.label}
          </MenuItem>
        );
      })}
    </Select>
  );
};

export const RaSelect: React.FC<RaSelectProps> = props => {
  const { t } = useLocale();
  const rules = {
    required: props.required ? t('This field is required') : undefined,
  };

  return (
    <Controller
      name={props.name}
      control={props.control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth error={!!error}>
          {props.label && (
            <RaLabel label={props.label} required={props.required} />
          )}
          {props.toggleButtons ? (
            <ToggleButtonGroup
              exclusive
              value={field.value}
              onChange={field.onChange}
              color="primary"
              disabled={props.disabled}
            >
              {props.options.map((option, index) => (
                <ToggleButton
                  key={option.value ?? ''}
                  value={option.value ?? ''}
                  disabled={props.disabled === true || option.disabled === true}
                  sx={theme => ({
                    border: `1px solid ${theme.palette.divider} !important`,
                    py: 1,
                    px: 1.5,
                    lineHeight: 1.65,
                    borderLeftColor:
                      index === 0
                        ? theme.palette.divider
                        : 'transparent ! important',
                  })}
                >
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          ) : (
            <SelectField
              clearable={props.clearable}
              field={field}
              options={props.options}
              disabled={props.disabled}
              multiple={props.multiple}
              renderValue={props.renderValue}
              renderOption={props.renderOption}
              placeholder={props.placeholder}
              loading={props.loading}
              emptyValueAsNull={props.emptyValueAsNull}
            />
          )}
          {(error?.message || props.helpText) && (
            <FormHelperText>{error?.message ?? props.helpText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};
